import Popover from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import Spinner from "@/components/ui/spinner";
import Tooltip from "@/components/ui/tooltip";
import type { TSlidesFolder } from "@/shared/types";
import {
  UpgradeLabel,
  useLockedAction,
} from "@components/plan-subscription-lock";
import { cn } from "@lib/cn";
import {
  IconChevronDown,
  IconUserFilled,
  IconUsersGroup,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { useState } from "react";
import { useGetFolders } from "../hooks";

export const FoldersSelect = ({
  folder,
  setFolder,
  disabledFolderId,
  disableTeamSlides,
}: {
  folder: { title: string; id: string };
  setFolder: (folder: { title: string; id: string }) => void;
  disabledFolderId?: string;
  disableTeamSlides?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { folders, isLoading } = useGetFolders();

  const { isLocked, plan, onLockedAction } = useLockedAction({
    feature: "save-as-team-slide",
  });

  const handleSelectFolder = (id: string, title: string) => {
    if (isLocked && id === "team-slides") return onLockedAction();
    setFolder({ title, id });
    setIsOpen(false);
  };

  return (
    <div>
      <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
        Folder
      </span>
      <div className="tw-relative">
        {isLoading && (
          <span className="tw-absolute tw-inset-0 tw-z-1 tw-size-full tw-bg-white/50"></span>
        )}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger>
            <div
              className={cn(
                "tw-relative tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue-100 !tw-outline-offset-0 tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-shadow-2xs",
                isOpen && "tw-outline-primary",
              )}
            >
              {["user-slides", "team-slides"].includes(folder.id) &&
                (() => {
                  const TriggerIcon: TablerIcon =
                    folder.id === "user-slides"
                      ? IconUserFilled
                      : IconUsersGroup;
                  return (
                    <TriggerIcon
                      size={20}
                      stroke={1.5}
                      className="tw-ml-1 tw-mr-2 tw-text-blue-500"
                    />
                  );
                })()}
              <span>{folder.title}</span>
              <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-items-center">
                {isLoading ? (
                  <Spinner />
                ) : (
                  <IconChevronDown
                    size={20}
                    stroke={1.5}
                    className={cn(
                      "tw-ml-auto tw-text-blue tw-transition-transform",
                      {
                        "tw-rotate-180": isOpen,
                      },
                    )}
                  />
                )}
              </div>
            </div>
          </Popover.Trigger>
          <Popover.Content className="tw-z-[10001] tw-w-[464px] tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue-100 !tw-outline-offset-0 tw-bg-white tw-shadow-sm">
            <ScrollArea maxHeight={248}>
              <div className="tw-p-2">
                <BaseFolder
                  type="user-slides"
                  onClick={() => handleSelectFolder("user-slides", "My slides")}
                  active={folder}
                  folders={folders?.userFolders}
                  disabledFolderId={disabledFolderId}
                  onSelectFolder={(id, title) => handleSelectFolder(id, title)}
                  isLocked={isLocked}
                  plan={plan}
                />
                <BaseFolder
                  type="team-slides"
                  isLocked={isLocked}
                  disabled={disableTeamSlides}
                  onClick={() =>
                    handleSelectFolder("team-slides", "Team slides")
                  }
                  active={folder}
                  folders={folders?.teamFolders}
                  disabledFolderId={disabledFolderId}
                  onSelectFolder={(id, title) => handleSelectFolder(id, title)}
                  plan={plan}
                />
              </div>
            </ScrollArea>
          </Popover.Content>
        </Popover>
      </div>
    </div>
  );
};

const BaseFolder = ({
  type,
  isLocked: isTeamLocked,
  disabled,
  plan,
  onClick,
  active,
  folders,
  onSelectFolder,
  disabledFolderId,
}: {
  type: "user-slides" | "team-slides";
  isLocked?: boolean;
  disabled?: boolean;
  plan?: string;
  onClick: () => void;
  active: { title: string; id: string };
  folders?: (TSlidesFolder & { children?: TSlidesFolder[] })[];
  onSelectFolder: (id: string, title: string) => void;
  disabledFolderId?: string;
}) => {
  const title = type === "user-slides" ? "My slides" : "Team slides";
  const RowIcon: TablerIcon =
    type === "user-slides" ? IconUserFilled : IconUsersGroup;
  const isLocked = type === "team-slides" && isTeamLocked;

  const handleFolderClick = (folderId: string, folderTitle: string) => {
    onSelectFolder(folderId, folderTitle);
  };

  const folderRow = (
    <div
      className={cn(
        "tw-flex tw-h-9 tw-w-full tw-items-center tw-rounded tw-px-3 tw-font-sans tw-text-[11px] tw-font-normal tw-text-blue tw-transition-colors",
        disabled
          ? "tw-cursor-not-allowed tw-opacity-50"
          : "tw-cursor-pointer hover:tw-bg-blue-50",
        isLocked &&
          !disabled &&
          "tw-outline-dashed tw-outline-1 tw-outline-blue-200 !tw-outline-offset-0 tw-bg-blue-50 tw-text-blue-500",
        active.id === type && !disabled && "tw-bg-primary-50 tw-text-primary",
        type === "team-slides" && "tw-mt-1",
      )}
      onClick={disabled ? undefined : onClick}
    >
      <RowIcon
        size={16}
        stroke={1.5}
        className={cn(
          "tw-mr-2 tw-text-blue-500",
          active.id === type && !disabled && "tw-text-primary",
        )}
      />
      {title}
      {isLocked && !disabled && plan && (
        <UpgradeLabel className="tw-ml-auto" feature="save-as-team-slide" />
      )}
    </div>
  );

  return (
    <div className="tw-relative">
      {disabled ? (
        <Tooltip placement="top">
          <Tooltip.Trigger asChild>{folderRow}</Tooltip.Trigger>
          <Tooltip.Content className="tw-z-[99999]">
            <Tooltip.Text>
              Your role doesn't allow saving to Team slides
            </Tooltip.Text>
          </Tooltip.Content>
        </Tooltip>
      ) : (
        folderRow
      )}
      {!disabled &&
        folders?.map((folder) => {
          if (disabledFolderId === folder._id) return null;
          return (
            <RecursiveFolder
              key={folder._id}
              folder={folder}
              onClick={handleFolderClick}
              active={active}
            />
          );
        })}
    </div>
  );
};

const RecursiveFolder = ({
  folder,
  level = 0,
  onClick,
  active,
}: {
  folder: TSlidesFolder & { children?: TSlidesFolder[] };
  level?: number;
  onClick?: (folderId: string, folderTitle: string) => void;
  active: { title: string; id: string };
}) => {
  const paddingLeft = 36 + level * 16;

  return (
    <>
      <div
        className={cn(
          "tw-mt-1 tw-flex tw-h-9 tw-max-w-[448px] tw-cursor-pointer tw-items-center tw-rounded tw-pr-2 tw-font-sans tw-text-[11px] tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
          active.id === folder._id && "tw-bg-primary-50 tw-text-primary",
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => onClick?.(folder._id, folder.title)}
      >
        <span className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
          {folder.title}
        </span>
      </div>
      {folder.children?.map((childFolder) => (
        <RecursiveFolder
          key={childFolder._id}
          folder={childFolder as TSlidesFolder & { children?: TSlidesFolder[] }}
          level={level + 1}
          onClick={onClick}
          active={active}
        />
      ))}
    </>
  );
};
